<template>
  <div v-if="ready" id="dash-student" class="content-with-spacing" :class="sidebarClass">
    <section class="student-container">
      <div class="student-details col-one panel panel-default">
        <student-info v-if="studentInfo"
          :studentInfo="studentInfo"
          :studentId="studentId"
          :studentName="studentName"
          :hubspotLink="hubspotLink"
          :selectedProgramme="selectedProgramme"
          :employers="employers"
          :programmeId="programmeIdFromRoute"
          :canUpdate="$permissions.hasPermission('manage_programmes')"
          @assign-employer="assignNewEmployer"
          @update-email="getStudentData"
        >
        </student-info>
      </div>
      <div v-if="programmes.length > 0" class="col-two">
        <learner-programmes
          :selected-programme="selectedProgramme"
          :programmes="programmes"
          v-model="programmeIdFromDropdown"
          :learner-status="learnerStatus"
          @freeze-click="handleFreezeClick"
          @assign-learner-status="assignNewLearnerStatus"
          @assign-graduation-date="assignNewEstimatedGraduationDate"
          @open-manage-ilr="openManageIlrSummaryPage"
        />
      </div>
      <div v-if="programmes.length > 0" class="col-three">
        <learner-profile-support
          :selected-programme="selectedProgramme"
          :student-coach="studentCoach"
          :student-data-mentor="studentDataMentor"
          :coaches="coaches"
          :data-mentors="dataMentors"
          @assign-coach="assignNewCoach"
          @assign-data-mentor="assignNewDataMentor"
        />
      </div>
    </section>
    <div class="line-manager-rag-container">
      <line-manager
        :learnerId="studentId"
        :canUpdate="$permissions.hasPermission('manage_learner_line_managers')"
        :line-manager="studentLineManager"
        :line-managers="lineManagers"
        :programme-member-id="currentProgrammeMemberId"
        @refresh-data="getStudentData"
      ></line-manager>
      <learner-rag
        v-if="$featureFlags.isEnabled('meeting-form-and-learner-profile-rag') && $permissions.hasPermission('view_user_rag_summary')"
        :student-id="studentId"
        :programme-id="programmeIdFromRoute"
      />
    </div>
    <div class="col-two col-two-container">
      <learner-logins
        v-if="$permissions.hasPermission('view_user_sessions')"
        :learnerId="studentId"
      ></learner-logins>
      <learner-links-panel
        v-if="showLearnerLinksPanel"
        :user-id="studentId"
        :programme-id="programmeIdFromRoute"
        :is-learner-support="isLearnerSupport"
      />
    </div>
    <div class="col-two col-two-container">
      <learner-activity-feed
        v-if="programmeResolved && $permissions.hasPermission('view_learner_activity_feeds')"
        :student-id="studentId"
        :student-name="studentName"
        :programme-id="programmeIdFromRoute"
        :programme-name="currentProgrammeName"
      />

      <k-panel v-if="hasViewNotePermissions && studentId && programmeIdFromRoute && programmeResolved"
        class="col-two-panel notes-panel"
        title="Notes"
        :showContent="showNotes" @toggle="showNotes = $event">
        <template #body>
          <notes-overview
            :allow-adding-new-notes="true"
            :userId="studentId"
            :programmeId="programmeIdFromRoute"
            :isLearnerSupport="isLearnerSupport"
            :posthog-no-capture="true">
            <template #tags>
              <div class="tags">
                <span class="danger">Not shared</span>
                <k-tooltip text="This note has not been shared with the employer" trigger-label="More information about note sharing status" position="right">
                  <i class="fas fa-info-circle" aria-hidden="true"></i>
                </k-tooltip>
              </div>
            </template>
          </notes-overview>
        </template>
      </k-panel>
    </div>
    <k-panel v-if="programmeResolved"
      :title="currentProgrammeName"
      :showContent="showProgramme"
      :lazy-body="true"
      @toggle="(val) => showProgramme = val"
    >
      <template #body>
        <programme-modules
          :programme-id="programmeIdFromRoute"
          :programme-name="currentProgrammeName"
          :student-id="studentId"
          :is-learner-support="isLearnerSupport"
          :programme-member-id="currentProgrammeMemberId">
        </programme-modules>
      </template>
    </k-panel>
    <learner-knowledge-base
      v-if="$permissions.hasPermission('view_knowledge_base') && studentId"
      :learner-user-id="studentId"
    />
  </div>
</template>

<style>
#dash-student .panel-default {
  box-shadow: var(--box-shadow);
}

/* k-table css */
.k-table-key-latest_feedback i,
.k-table-key-project i {
  float: right;
}

.student-container h2,
.line-manager-rag-container h2 {
  margin-top: 0;
}

.student-container i.fas.fa-edit:hover {
  color: var(--kate-type-blue);
  transition: all 0.3s;
}

.student-container i.fas.fa-times-circle {
  color: var(--kate-danger-alt);
}

.student-container i.fas.fa-times-circle:hover {
  color: var(--kate-danger);
}

.student-container i.fas.fa-check:hover {
  color: var(--kate-success);
}

.student-info-header figure.avatar {
  width: 40px;
  min-width: 40px;
  height: 40px;
}

#dash-student .student-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

#dash-student .content-spinner {
  text-align: center;
  font-size: 1.5em;
  width: 100%;
}

#dash-student {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

#dash-student > * {
  margin-top: 0;
  margin-bottom: 0;
}

#dash-student .student-container {
  margin-top: 20px;
}

i.fa-edit {
  color: var(--kate-type-light);
}
</style>

<style scoped>
.line-manager {
  padding: 15px;
  align-items: stretch;
}

/* Student-container */
.student-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 15px;
  margin: 0;
  align-items: stretch;
  container-type: inline-size;
}

/* When the grid wraps to two columns, let the third panel span the full row */
@container (max-width: 869px) {
  .student-container > .col-three:last-child {
    grid-column: 1 / -1;
  }
}

.line-manager-rag-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 15px;
  align-items: stretch;
  margin: 0;
}

.student-container .panel {
  padding: 15px;
  margin: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
}

.student-details,
.col-two,
.col-three,
.line-manager-rag-container > * {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.col-two :deep(> *),
.col-three :deep(> *) {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.line-manager-rag-container > * {
  height: 100%;
}

.line-manager-rag-container :deep(.panel) {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.line-manager-rag-container :deep(.panel-body) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.student-details :deep(.student-info) {
  flex: 1;
  height: 100%;
}

/* Activity and notes */
.col-two-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;
  width: 100%;
  align-items: stretch;
}

.col-two-container > :only-child {
  grid-column: 1 / -1;
}

.col-two-container > * {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.col-two-container :deep(.panel) {
  flex: 1;
  height: 100%;
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.col-two-container :deep(.panel-body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.col-two-container :deep(.panel-header) {
  flex-shrink: 0;
}

.col-two-container :deep(.panel-content) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.col-two :deep(.col-two-panel-narrow) {
  flex: 1 1 16%;
}

.col-two :deep(.col-two-panel) {
  flex: 2;
  margin-bottom: 20px;
}

/* Misc */
.panel-heading {
  padding-left: 0;
}

.heading-container h2 {
  margin: 0;
  padding: 10px 0;
}

.heading-container + div {
  padding-top: 15px;
}

.heading-container.user-activity-container {
  display: flex;
  justify-content: space-between;
}

h2 > i.fas.fa-angle-up,
h2 > i.fas.fa-angle-down {
  float: right;
  font-size: 1.5rem;
}

.archive-toggle {
  display: flex;
  align-items: center;
}

.archive-toggle > span:first-child {
  margin-right: 0.5em;
}

@media only screen and (max-width: 1200px) {
  .col-two-container {
    grid-template-columns: 1fr;
  }

  .col-two-container :deep(.col-two-panel) {
    margin-bottom: 0;
  }
}
</style>

<script>
import SidebarMixin from '@mixins/sidebar-mixin';
import useGlobalStore from '@stores/global';
import TimeMixin from '@mixins/time-mixins';
import CsvMixin from '@mixins/csv-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KPanel from '@base-components/k-panel.vue';
import { KTooltip } from '@kate/components';
import { sortObjectArray } from '@utils/sort-by-object-property';
import ProgrammeModules from './student_components/student-programme-modules.vue';
import LearnerActivityFeed from './learner-activity-feed.vue';
import LearnerKnowledgeBase from './student_components/learner-knowledge-base.vue';
import NotesOverview from './notes-overview.vue';
import StudentInfo from './student-info.vue';
import LearnerProgrammes from './learner-programmes.vue';
import LearnerProfileSupport from './learner-profile-support.vue';
import LearnerRag from './learner-rag.vue';
import LearnerLogins from './learner-logins.vue';
import LearnerLinksPanel from './learner-links-panel.vue';
import LineManager from './line-manager.vue';

export default {
  components: {
    KTooltip,
    ProgrammeModules,
    LearnerActivityFeed,
    KPanel,
    NotesOverview,
    StudentInfo,
    LearnerProgrammes,
    LearnerProfileSupport,
    LearnerRag,
    LearnerLogins,
    LearnerLinksPanel,
    LineManager,
    LearnerKnowledgeBase,
  },

  mixins: [TimeMixin, CsvMixin, PageReadyMixin, SidebarMixin],

  data() {
    return {
      store: useGlobalStore(),
      studentName: '',
      studentInfo: undefined,
      studentDataReady: false,
      programmes: [],
      programmesReady: false,
      programmeIdFromDropdown: undefined,
      coaches: [],
      coachesReady: false,
      employers: [],
      employersReady: false,
      dataMentors: [],
      dataMentorsReady: false,
      lineManagers: [],
      updateInProgress: false,
      showProgramme: false,
      learnerStatus: ['Active', 'Graduated', 'On-break', 'Leaver', 'Non-starter', 'Entered EPA'],
      showNotes: false,
      showActivity: false,
      showKB: false,
    };
  },

  beforeMount() {
    this.mountCallback();
  },

  watch: {
    ready() {
      if (this.programmes.length > 0) {
        this.programmeIdFromDropdown = this.selectProgrammeIdOnStart();
      } else if (this.ready) {
        this.$ktoast.info('Learner is not a member of any programme');
      }
    },

    programmeIdFromDropdown(newProgrammeId) {
      const selectedProgramme = this.programmes.find(p => p.id === newProgrammeId);
      if (selectedProgramme) {
        if (selectedProgramme.is_frozen) {
          this.$ktoast.warning('The learner\'s access to this programme is currently frozen');
        }
      }
      this.$router.replace({ query: { programmeId: newProgrammeId } });
    },

    studentId(newStudentId) {
      // Don't fetch data if studentId is invalid (e.g., after redirecting to 403 page)
      if (!newStudentId || Number.isNaN(newStudentId)) {
        return;
      }
      // Reset the page when the learner changes
      this.resetState();
      this.mountCallback();
    },

    programmeIdFromRoute(programmeId) {
      if (programmeId || !this.programmeIdFromDropdown) {
        return;
      }
      this.$router.replace({ query: { programmeId: this.programmeIdFromDropdown } });
    },
  },

  computed: {
    programmeResolved() {
      return this.programmesReady && this.selectedProgramme && this.programmeIdFromRoute;
    },

    programmeIdFromRoute() {
      return this.$route.query?.programmeId ? Number(this.$route.query.programmeId) : undefined;
    },

    isLearnerSupport() {
      const currentUserId = this.store.userId;
      return Boolean((this.studentCoach?.user_id === currentUserId) || (this.studentDataMentor?.user_id === currentUserId));
    },

    hasViewNotePermissions() {
      return this.$permissions.hasPermission('view_user_notes') || this.isLearnerSupport;
    },

    showLearnerLinksPanel() {
      return this.$featureFlags.isEnabled('learner-profile-links-dashboard')
        && Boolean(this.programmeIdFromRoute)
        && Boolean(this.programmeResolved)
        && (this.$permissions.hasPermission('view_learner_links'));
    },

    ready() {
      return this.studentDataReady && this.programmesReady && !this.updateInProgress
      && this.coachesReady && this.dataMentorsReady && this.employersReady;
    },

    studentId() {
      return Number(this.$route.params.studentId);
    },

    studentCoach() {
      if (this.studentInfo && this.studentInfo.coaches && this.studentInfo.coaches.length > 0) {
        return this.studentInfo.coaches.find(x => x.programme_id === this.programmeIdFromRoute);
      }
      return undefined;
    },

    studentDataMentor() {
      if (this.studentInfo && this.studentInfo.data_mentors && this.studentInfo.data_mentors.length > 0) {
        return this.studentInfo.data_mentors.find(x => x.programme_id === this.programmeIdFromRoute);
      }
      return undefined;
    },

    studentLineManager() {
      if (this.studentInfo && this.studentInfo.line_managers && this.studentInfo.line_managers.length > 0) {
        return this.studentInfo.line_managers.find(x => x.programme_id === this.programmeIdFromRoute);
      }
      return undefined;
    },

    hubspotLink() {
      if (!this.programmes.length) {
        return undefined;
      }
      return this.programmes.find(x => x.id === this.programmeIdFromRoute)?.hubspot_link;
    },

    currentProgrammeName() {
      if (!this.programmes.length) {
        return undefined;
      }
      return this.programmes.find(x => x.id === this.programmeIdFromRoute)?.name;
    },

    currentProgrammeMemberId() {
      if (!this.programmes.length) {
        return undefined;
      }
      return this.programmes.find(x => x.id === this.programmeIdFromRoute)?.programme_member_id;
    },

    selectedProgramme() {
      if (this.programmes.length === 0) {
        return undefined;
      }
      return this.programmeIdFromRoute ? this.programmes.find(x => x.id === this.programmeIdFromRoute) : this.programmes[0];
    },
  },

  methods: {
    resetState() {
      this.studentName = '';
      this.studentInfo = undefined;
      this.studentDataReady = false;
      this.programmes = [];
      this.programmesReady = false;
      this.programmeIdFromDropdown = undefined;
      this.coaches = [];
      this.coachesReady = false;
      this.employers = [];
      this.employersReady = false;
      this.dataMentors = [];
      this.dataMentorsReady = false;
      this.lineManagers = [];
      this.updateInProgress = false;
      this.showProgramme = false;
    },

    mountCallback() {
      this.$Loading.start();
      this.getStudentProgrammes();
      this.getStudentData();
      this.getCoaches();
      this.getDataMentors();
      this.getEmployers();
    },

    selectProgrammeIdOnStart() {
      if (this.programmeIdFromRoute) {
        const pId = parseInt(this.programmeIdFromRoute, 10);
        if (Number.isInteger(pId) && this.programmes.map(p => p.id).indexOf(pId) !== -1) {
          return pId;
        }
        const programmeId = this.preferredProgrammeId();
        const chosen = this.programmes.find(p => p.id === programmeId);
        const fallback = chosen?.is_apprenticeship
          ? 'the most recent apprenticeship programme'
          : 'the most recent programme';
        this.$logger.warn(`Learner not in specified programme - defaulting to ${fallback}`);
        return programmeId;
      }
      return this.preferredProgrammeId();
    },

    preferredProgrammeId() {
      const nonFrozenProgrammes = this.programmes.filter(p => !p.is_frozen);
      const candidates = nonFrozenProgrammes.length > 0 ? nonFrozenProgrammes : this.programmes;
      const apprenticeships = candidates.filter(p => p.is_apprenticeship);
      const pool = apprenticeships.length > 0 ? apprenticeships : candidates;
      return sortObjectArray(pool, 'start_date', true)[0].id;
    },

    registerCrumbs() {
      this.$crumbs.register([{
        text: this.studentName || this.studentId,
        active: true,
      }]);
    },

    getCoaches() {
      this.coachesReady = false;
      if (!this.$permissions.hasPermission('manage_coaches')) {
        this.coachesReady = true;
        return;
      }
      this.$logger.info('Getting coaches');
      this.$http.get('/api/profile/coach').then(res => {
        this.$logger.info('Successfully retrieved coaches');
        this.coaches = res.data.coaches;
      }).catch(error => {
        if (this.$http.isWarning(error)) {
          this.$logger.warn('There was an error while attempting to retrieve coaches', { studentId: this.studentId }, error);
        } else {
          this.$logger.error('There was an error while attempting to retrieve coaches', { studentId: this.studentId }, error);
        }
        this.$ktoast.showError(error);
      }).then(() => {
        this.coachesReady = true;
      });
    },
    getDataMentors() {
      this.dataMentorsReady = false;
      if (!this.$permissions.hasPermission('manage_mentors')) {
        this.dataMentorsReady = true;
        return;
      }
      this.$logger.info('Getting data mentors');
      this.$http.get('/api/profile/data_mentor').then(res => {
        this.$logger.info('Successfully retrieved data mentors');
        this.dataMentors = res.data.data_mentors;
      }).catch(error => {
        if (this.$http.isWarning(error)) {
          this.$logger.warn('There was an error while attempting to retrieve data mentors', { studentId: this.studentId }, error);
        } else {
          this.$logger.error('There was an error while attempting to retrieve data mentors', { studentId: this.studentId }, error);
        }
        this.$ktoast.showError(error);
      }).then(() => {
        this.dataMentorsReady = true;
      });
    },
    getEmployers() {
      this.employersReady = false;
      if (!this.$permissions.hasPermission('manage_programmes')) {
        this.employersReady = true;
        return;
      }
      this.$logger.info('Getting employers');
      this.$http.get('/api/profile/employers').then(res => {
        this.$logger.info('Successfully retrieved employers');
        this.employers = res.data;
      }).catch(error => {
        if (this.$http.isWarning(error)) {
          this.$logger.warn('There was an error while attempting to retrieve employers', { studentId: this.studentId }, error);
        } else {
          this.$logger.error('There was an error while attempting to retrieve employers', { studentId: this.studentId }, error);
        }
        this.$ktoast.showError(error);
      }).then(() => {
        this.employersReady = true;
      });
    },
    getStudentProgrammes() {
      this.$logger.info('Getting student programmes', { studentId: this.studentId }, true);
      this.programmesReady = false;
      this.$http.get(`/api/profile/user/${this.studentId}/programmes?dashboard=true`)
        .then(result => {
          this.programmes = result.data.programmes;
        }).catch(error => {
          this.$logger.error('Error getting student programmes', { studentId: this.studentId }, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.programmesReady = true;
        });
    },
    getStudentData() {
      this.$logger.info('Getting student profile', { studentId: this.studentId }, true);
      this.studentDataReady = false;
      this.$http.get(`/api/profile/users/${this.studentId}`)
        .then(result => {
          this.studentName = result.data.full_name;
          this.studentInfo = result.data;
          this.registerCrumbs();
          this.$logger.info('Got student profile', { studentId: this.studentId });
        }).catch(error => {
          if (this.$http.errIn(error, [403])) {
            this.$logger.warn('User does not have permission to view this learner', { studentId: this.studentId }, error);
            this.$router.push({ name: '403' });
            return;
          }
          this.$logger.error('Error getting student profile', { studentId: this.studentId }, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.studentDataReady = true;
        });
    },
    assignNewLearnerStatus(newLearnerStatus) {
      this.updateInProgress = true;
      const payload = { learner_status: newLearnerStatus };
      this.$logger.info('Assigning new learner status to user', { learnerStatus: payload }, true);
      this.$http.put(
        `/api/profile/admin/programmes/${this.selectedProgramme.id}/users/${this.studentId}/learner_status`,
        payload,
      ).then(() => {
        this.$logger.info('Learner status successfully updated', { learnerStatus: payload }, true);
        this.getStudentProgrammes();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('There was an error while attempting to update learner status', undefined, err);
        } else {
          this.$logger.error('There was an error while attempting to update learner status', undefined, err);
        }
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },
    assignNewEstimatedGraduationDate(newGraduationDate) {
      this.updateInProgress = true;
      const payload = { egd: this.formatDate(newGraduationDate) };
      this.$logger.info('Assigning new estimated graduation date to user', {
        studentId: this.studentId,
        programmeId: this.selectedProgramme.id,
        oldDate: this.selectedProgramme.estimated_graduation_date,
        newDate: payload.egd,
      }, true);
      this.$http.put(
        `/api/profile/admin/programmes/${this.selectedProgramme.id}/users/${this.studentId}/graduation_date`,
        payload,
      ).then(() => {
        this.$logger.info('Estimated graduation date successfully updated', {
          studentId: this.studentId,
          programmeId: this.selectedProgramme.id,
        });
        this.getStudentProgrammes();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Error updating estimated graduation date', {
            studentId: this.studentId,
            programmeId: this.selectedProgramme.id,
            error: err.message,
          });
        } else {
          this.$logger.error('Error updating estimated graduation date', {
            studentId: this.studentId,
            programmeId: this.selectedProgramme.id,
            error: err.message,
          });
        }
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },
    assignNewCoach(newCoachId) {
      this.updateInProgress = true;
      const payload = {
        coach_and_mentors: [
          {
            user_id: parseInt(this.studentId, 10),
            coach_user_id: parseInt(newCoachId, 10),
            data_mentor_user_id: this.studentDataMentor?.user_id || null,
            programme_id: this.programmeIdFromRoute,
          },
        ],
      };
      this.$logger.info('Assigning new coach to user', { userCoaches: payload }, true);
      this.$http.put('/api/profile/learner_support/bulk', payload).then(() => {
        this.$logger.info('Coach successfully assigned');
        this.getStudentData();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('There was an error while attempting to assign new coach', undefined, err);
        } else {
          this.$logger.error('There was an error while attempting to assign new coach', undefined, err);
        }
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },
    assignNewDataMentor(newDataMentorId) {
      this.updateInProgress = true;
      const payload = {
        coach_and_mentors: [
          {
            user_id: parseInt(this.studentId, 10),
            coach_user_id: this.studentCoach?.user_id || null,
            data_mentor_user_id: parseInt(newDataMentorId, 10),
            programme_id: this.programmeIdFromRoute,
          },
        ],
      };
      this.$logger.info('Assigning new data mentor to user', { userDataMentors: payload }, true);
      this.$http.put('/api/profile/learner_support/bulk', payload).then(() => {
        this.$logger.info('Data mentor successfully assigned');
        this.getStudentData();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('There was an error while attempting to assign new data mentor', undefined, err);
        } else {
          this.$logger.error('There was an error while attempting to assign new data mentor', undefined, err);
        }
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },
    assignNewEmployer(newEmployerId) {
      this.updateInProgress = true;
      this.$logger.info('Assigning new employer to user', undefined, true);
      this.$http.put(`/api/profile/user/${this.studentId}/programme/${this.programmeIdFromRoute}/employer/${newEmployerId}`).then(() => {
        this.$logger.info('Employer successfully assigned');
        this.getStudentData();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('There was an error while attempting to assign employer', undefined, err);
        } else {
          this.$logger.error('There was an error while attempting to assign employer', undefined, err);
        }
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },
    handleFreezeClick() {
      if (this.selectedProgramme.is_frozen) {
        this.unfreezeProgramme();
        return;
      }
      this.freezeProgramme();
    },
    freezeProgramme() {
      this.updateInProgress = true;
      this.$logger.info('Freezing user on programme', { userId: this.studentId, programmeId: this.selectedProgramme.id }, true);
      this.$http.put(`/api/profile/user/${this.studentId}/programmes/${this.selectedProgramme.id}/freeze`).then(() => {
        this.$logger.info('User access to programme frozen');
        this.getStudentProgrammes();
      }).catch(err => {
        this.$logger.error('There was an error while attempting to freeze user access to programme', { userId: this.studentId, programmeId: this.selectedProgramme.id, err });
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },
    unfreezeProgramme() {
      this.updateInProgress = true;
      this.$logger.info('Unfreezing user on programme', { userId: this.studentId, programmeId: this.selectedProgramme.id }, true);
      this.$http.put(`/api/profile/user/${this.studentId}/programmes/${this.selectedProgramme.id}/unfreeze`).then(() => {
        this.$logger.info('User access to programme unfrozen');
        this.getStudentProgrammes();
      }).catch(err => {
        this.$logger.error('There was an error while attempting to Unfreezing user access to programme', { userId: this.studentId, programmeId: this.selectedProgramme.id, err });
        this.$ktoast.showError(err);
      }).then(() => {
        this.updateInProgress = false;
      });
    },

    openManageIlrSummaryPage() {
      this.$router.push({
        name: 'manage_ilr_summary',
        params: {
          studentId: this.studentId,
        },
      });
    },
  },
};
</script>
